import { useState, useEffect } from 'react'
import {
  Plus,
  Edit2,
  Trash2,
  Eye,
  EyeOff,
  Newspaper,
  AlertCircle,
  CheckCircle,
  Loader2,
  ExternalLink,
  Sparkles,
  Send,
  Mail,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Link } from 'react-router-dom'
import { useCollection, useFileUpload } from '../../hooks/useFirestore'
import { useAuth } from '../../context/AuthContext'
import { callFunction } from '../../services/firebase'
import { slugify, formatDate } from '../../utils/helpers'
import {
  Button,
  Modal,
  Input,
  Textarea,
  Select,
  Card,
  Badge,
  Checkbox,
} from '../../components/common'

const categoryOptions = [
  { value: 'devotional', label: 'Devotional' },
  { value: 'sermon-recap', label: 'Sermon Recap' },
  { value: 'church-news', label: 'Church News' },
  { value: 'testimony', label: 'Testimony' },
  { value: 'teaching', label: 'Teaching' },
]

const emptyPost = {
  title: '',
  slug: '',
  excerpt: '',
  body: '',
  coverImageUrl: '',
  author: '',
  category: '',
  tags: [],
  isPublished: false,
  publishedAt: null,
}

const PostForm = ({ post, onSave, onClose }) => {
  const { user, userProfile } = useAuth()
  const [formData, setFormData] = useState(
    post || {
      ...emptyPost,
      author:
        userProfile?.displayName ||
        user?.displayName ||
        user?.email?.split('@')[0] ||
        '',
    }
  )
  const [saving, setSaving] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [slugTouched, setSlugTouched] = useState(!!post?.slug)
  const { upload, uploading } = useFileUpload()

  // Auto-generate slug from title until user manually edits the slug
  useEffect(() => {
    if (!slugTouched && formData.title) {
      setFormData((prev) => ({ ...prev, slug: slugify(prev.title) }))
    }
  }, [formData.title, slugTouched])

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const result = await upload(file, `blog/${Date.now()}_${file.name}`)
    if (result.success) {
      setFormData({ ...formData, coverImageUrl: result.url })
    }
  }

  const handleTagsChange = (value) => {
    const tags = value
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
    setFormData({ ...formData, tags })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    const payload = {
      ...formData,
      slug: slugify(formData.slug || formData.title),
    }
    // Set publishedAt the first time it's published
    if (payload.isPublished && !payload.publishedAt) {
      payload.publishedAt = new Date().toISOString()
    }
    await onSave(payload)
    setSaving(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Input
        label="Post Title"
        required
        value={formData.title}
        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        placeholder="Walking in Faith Through Hard Seasons"
      />

      <Input
        label="URL Slug"
        required
        value={formData.slug}
        onChange={(e) => {
          setSlugTouched(true)
          setFormData({ ...formData, slug: e.target.value })
        }}
        helperText="Auto-generated from title. Used in the URL: /blog/your-slug"
        placeholder="walking-in-faith-through-hard-seasons"
      />

      <Textarea
        label="Excerpt"
        rows={3}
        required
        value={formData.excerpt}
        onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
        placeholder="A short summary shown on the blog list and at the top of the post."
      />

      <div className="grid md:grid-cols-2 gap-4">
        <Select
          label="Category"
          required
          options={categoryOptions}
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
        />
        <Input
          label="Author"
          required
          value={formData.author}
          onChange={(e) => setFormData({ ...formData, author: e.target.value })}
          placeholder="Pastor John"
        />
      </div>

      <Input
        label="Tags (comma-separated, optional)"
        value={(formData.tags || []).join(', ')}
        onChange={(e) => handleTagsChange(e.target.value)}
        placeholder="faith, encouragement, prayer"
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Cover Image
        </label>
        <div className="flex items-center gap-4">
          {formData.coverImageUrl && (
            <img
              src={formData.coverImageUrl}
              alt="Cover preview"
              className="w-32 h-20 object-cover rounded-lg border border-gray-200"
            />
          )}
          <label className="px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
            <span className="text-sm">
              {uploading ? 'Uploading...' : formData.coverImageUrl ? 'Replace Image' : 'Upload Image'}
            </span>
            <input
              type="file"
              accept="image/*"
              onChange={handleCoverUpload}
              className="hidden"
            />
          </label>
          {formData.coverImageUrl && (
            <button
              type="button"
              onClick={() => setFormData({ ...formData, coverImageUrl: '' })}
              className="text-sm text-red-600 hover:underline"
            >
              Remove
            </button>
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Body (Markdown) <span className="text-red-500">*</span>
          </label>
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="text-sm text-primary-600 hover:underline flex items-center gap-1"
          >
            <Eye className="w-4 h-4" />
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
        </div>
        <div className={showPreview ? 'grid md:grid-cols-2 gap-4' : ''}>
          <textarea
            value={formData.body}
            onChange={(e) => setFormData({ ...formData, body: e.target.value })}
            rows={16}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder={'## A Section Heading\n\nWrite your post in **Markdown**. Use *italics*, lists:\n\n- Point one\n- Point two\n\n> Quote a scripture here.\n\n[Link to something](https://example.com)'}
          />
          {showPreview && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 overflow-y-auto max-h-[420px]">
              <div className="prose prose-sm max-w-none">
                {formData.body ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {formData.body}
                  </ReactMarkdown>
                ) : (
                  <p className="text-gray-400 italic">Preview will appear here…</p>
                )}
              </div>
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
          <Sparkles className="w-3 h-3" />
          Supports headings (##), bold (**), italics (*), lists, blockquotes (&gt;), links, images, and tables.
        </p>
      </div>

      <Checkbox
        label="Publish this post (visible on the public blog)"
        checked={formData.isPublished}
        onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
      />

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={saving}>
          {post ? 'Update Post' : 'Create Post'}
        </Button>
      </div>
    </form>
  )
}

const BlogManager = () => {
  const { user } = useAuth()
  const [showModal, setShowModal] = useState(false)
  const [editingPost, setEditingPost] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [actionError, setActionError] = useState(null)
  const [actionSuccess, setActionSuccess] = useState(null)
  const [notifyConfirm, setNotifyConfirm] = useState(null)
  const [testEmailFor, setTestEmailFor] = useState(null)
  const [testEmailAddr, setTestEmailAddr] = useState('')
  const [notifyBusy, setNotifyBusy] = useState(false)

  const { data: posts, loading, error, add, update, remove } = useCollection('blogPosts', {
    orderByField: 'createdAt',
    orderDirection: 'desc',
  })

  const showMessage = (message, isError = false) => {
    if (isError) {
      setActionError(message)
      setTimeout(() => setActionError(null), 5000)
    } else {
      setActionSuccess(message)
      setTimeout(() => setActionSuccess(null), 3000)
    }
  }

  const handleSave = async (data) => {
    try {
      // Check for duplicate slug (excluding the post being edited)
      const duplicate = (posts || []).find(
        (p) => p.slug === data.slug && p.id !== editingPost?.id
      )
      if (duplicate) {
        throw new Error('A post with this slug already exists. Please choose a unique slug.')
      }

      if (editingPost?.id) {
        const result = await update(editingPost.id, data)
        if (result.success === false) throw new Error(result.error?.message || 'Failed to update')
        showMessage('Post updated successfully!')
      } else {
        const result = await add(data)
        if (result.success === false) throw new Error(result.error?.message || 'Failed to create')
        showMessage('Post created!')
      }
      setShowModal(false)
      setEditingPost(null)
    } catch (err) {
      console.error('Save error:', err)
      showMessage(err.message || 'Failed to save post.', true)
    }
  }

  const handleDelete = async () => {
    if (!deleteConfirm) return
    try {
      const result = await remove(deleteConfirm)
      if (result.success === false) throw new Error(result.error?.message || 'Failed to delete')
      showMessage('Post deleted.')
      setDeleteConfirm(null)
    } catch (err) {
      console.error('Delete error:', err)
      showMessage(err.message || 'Failed to delete post.', true)
      setDeleteConfirm(null)
    }
  }

  const handleNotify = async () => {
    if (!notifyConfirm) return
    setNotifyBusy(true)
    try {
      const result = await callFunction('notifyBlogSubscribers', { postId: notifyConfirm.id })
      showMessage(`Sent to ${result.sent} subscriber${result.sent === 1 ? '' : 's'}${result.failed ? ` (${result.failed} failed)` : ''}.`)
      setNotifyConfirm(null)
    } catch (err) {
      console.error('Notify error:', err)
      showMessage(err.message || 'Failed to send notifications.', true)
    } finally {
      setNotifyBusy(false)
    }
  }

  const handleTestEmail = async () => {
    if (!testEmailFor || !testEmailAddr) return
    setNotifyBusy(true)
    try {
      await callFunction('notifyBlogSubscribers', {
        postId: testEmailFor.id,
        testEmail: testEmailAddr,
      })
      showMessage(`Test email sent to ${testEmailAddr}.`)
      setTestEmailFor(null)
      setTestEmailAddr('')
    } catch (err) {
      console.error('Test email error:', err)
      showMessage(err.message || 'Failed to send test email.', true)
    } finally {
      setNotifyBusy(false)
    }
  }

  const togglePublish = async (post) => {
    try {
      const patch = { isPublished: !post.isPublished }
      if (!post.isPublished && !post.publishedAt) {
        patch.publishedAt = new Date().toISOString()
      }
      const result = await update(post.id, patch)
      if (result.success === false) throw new Error(result.error?.message || 'Failed to update')
      showMessage(`Post ${post.isPublished ? 'unpublished' : 'published'}!`)
    } catch (err) {
      console.error('Toggle error:', err)
      showMessage(err.message || 'Failed to update post status.', true)
    }
  }

  return (
    <div className="space-y-6">
      {/* Alerts */}
      {actionError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {actionError}
        </div>
      )}
      {actionSuccess && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          {actionSuccess}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-dark-100">Blog</h1>
          <p className="text-gray-500">Write, edit, and publish blog posts</p>
          {error && (
            <p className="text-red-500 text-sm mt-1">Error loading data: {error.message}</p>
          )}
        </div>
        <Button
          variant="primary"
          icon={Plus}
          onClick={() => {
            setEditingPost(null)
            setShowModal(true)
          }}
        >
          New Post
        </Button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      )}

      {/* Empty */}
      {!loading && (!posts || posts.length === 0) && (
        <Card animate={false} className="text-center py-16">
          <Newspaper className="w-14 h-14 mx-auto mb-4 text-gray-300" />
          <h3 className="text-xl font-heading font-bold text-dark-100 mb-2">
            No blog posts yet
          </h3>
          <p className="text-gray-500 mb-6">
            Create your first post to get the blog started.
          </p>
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => {
              setEditingPost(null)
              setShowModal(true)
            }}
          >
            Create First Post
          </Button>
        </Card>
      )}

      {/* List */}
      {!loading && posts?.length > 0 && (
        <Card animate={false} className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Title</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Category</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Author</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Date</th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {posts.map((post) => (
                  <tr key={post.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {post.coverImageUrl ? (
                          <img
                            src={post.coverImageUrl}
                            alt=""
                            className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary-100 to-secondary-100 flex items-center justify-center flex-shrink-0">
                            <Newspaper className="w-5 h-5 text-primary-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-dark-100">{post.title}</p>
                          <p className="text-xs text-gray-500">/{post.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 capitalize">
                      {post.category?.replace('-', ' ') || '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{post.author}</td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        {post.isPublished ? (
                          <Badge variant="success" size="sm">Published</Badge>
                        ) : (
                          <Badge variant="gray" size="sm">Draft</Badge>
                        )}
                        {post.notifiedAt && (
                          <span className="text-xs text-emerald-600 flex items-center gap-1">
                            <Send className="w-3 h-3" />
                            Sent to {post.notifyCount || 0}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {post.publishedAt
                        ? formatDate(post.publishedAt?.toDate?.() || post.publishedAt, 'MMM d, yyyy')
                        : 'Unpublished'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {post.isPublished && (
                          <Link
                            to={`/blog/${post.slug}`}
                            target="_blank"
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="View on site"
                          >
                            <ExternalLink className="w-5 h-5" />
                          </Link>
                        )}
                        {post.isPublished && (
                          <button
                            onClick={() => {
                              setTestEmailAddr(user?.email || '')
                              setTestEmailFor(post)
                            }}
                            className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg"
                            title="Send test email"
                          >
                            <Mail className="w-5 h-5" />
                          </button>
                        )}
                        {post.isPublished && (
                          <button
                            onClick={() => setNotifyConfirm(post)}
                            disabled={!!post.notifiedAt}
                            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                            title={post.notifiedAt ? `Already notified (${post.notifyCount || 0} sent)` : 'Notify subscribers'}
                          >
                            <Send className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => togglePublish(post)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                          title={post.isPublished ? 'Unpublish' : 'Publish'}
                        >
                          {post.isPublished ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                        <button
                          onClick={() => {
                            setEditingPost(post)
                            setShowModal(true)
                          }}
                          className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                          title="Edit"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(post.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Form Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setEditingPost(null)
        }}
        title={editingPost ? 'Edit Post' : 'New Blog Post'}
        size="xl"
      >
        <PostForm
          post={editingPost}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false)
            setEditingPost(null)
          }}
        />
      </Modal>

      {/* Delete Confirm */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Post"
        size="sm"
      >
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete this post? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>

      {/* Notify Subscribers Confirm */}
      <Modal
        isOpen={!!notifyConfirm}
        onClose={() => !notifyBusy && setNotifyConfirm(null)}
        title="Notify Newsletter Subscribers"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Send <strong>{notifyConfirm?.title}</strong> to all active newsletter
            subscribers via email?
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            This action cannot be undone. Once sent, this post will be marked as
            notified and cannot be sent again.
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="ghost"
              onClick={() => setNotifyConfirm(null)}
              disabled={notifyBusy}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleNotify} loading={notifyBusy} icon={Send}>
              Send Now
            </Button>
          </div>
        </div>
      </Modal>

      {/* Test Email */}
      <Modal
        isOpen={!!testEmailFor}
        onClose={() => !notifyBusy && setTestEmailFor(null)}
        title="Send Test Email"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Preview <strong>{testEmailFor?.title}</strong> by sending a test to a
            single address.
          </p>
          <Input
            label="Send test to"
            type="email"
            required
            value={testEmailAddr}
            onChange={(e) => setTestEmailAddr(e.target.value)}
            placeholder="you@example.com"
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="ghost"
              onClick={() => setTestEmailFor(null)}
              disabled={notifyBusy}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleTestEmail}
              loading={notifyBusy}
              disabled={!testEmailAddr}
              icon={Mail}
            >
              Send Test
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default BlogManager
